import { some, pick } from 'underscore';
import Promise from 'bluebird';
/*
 * Captures:
 * [0]: whole match
 * [1]: keyword text
 * [2]: default value
 */
const keywordRuleSyntax = /\{(keyword)\s*:([^{}]*)}/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: keyword text (could be incomplete)
 * [2]: default value (could be empty)
 */
const keywordRuleInProgressSyntax = /{(k(?:e|$)(?:y|$)(?:w|$)(?:o|$)(?:r|$)(?:d|$))(?::|$)([^}]*)/i;

/*
 * Matches Title Case keyword text (could be incomplete)
 */
const keywordRuleTitleCaseSyntax = /KeyW(?:o|$)(?:r|$)(?:d|$)/;

/**
 * Looks for keyword rules in given text and substitutes them with their values.
 * @param {string} text - input text
 * @returns {string} text after substitutions
 */
export function evaluateKeywordRules(text) {
  if (text && text.length > 0) {
    const keywordReplaceSyntax = new RegExp(keywordRuleSyntax.source, 'gi');

    return Promise.resolve(text.replace(keywordReplaceSyntax, (match, p1, p2) => {
      if (p2 !== '') return p2;
      return match;
    }));
  }
  return Promise.resolve(text);
}

/**
 * Supported keyword case types
 */
const caseTypes = {
  titleCase: 'TitleCase',
  sentenceCase: 'SentenceCase',
  lowerCase: 'LowerCase',
};

/**
 * Check an incomplete string of "keyword" and determine the case
 * @param {string} text - text input
 * @returns {string} Case type: TitleCase, SentenceCase, LowerCase
 */
function getKeywordCase(text) {
  if (keywordRuleTitleCaseSyntax.test(text)) {
    return caseTypes.titleCase;
  } else if (text[0] === 'K') {
    return caseTypes.sentenceCase;
  }

  return caseTypes.lowerCase;
}

/**
 * Looks for a keyword rule definition in given text starting from passed index.
 *   If found, it parses the definition.
 * @param {string} text - text to search in
 * @param {string} syntax - regex syntax use for matching
 * @param {int} startIndex - starts searching at this index
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseKeywordRule(text, syntax, startIndex, cursorIndex) {
  const tokens = syntax.exec(text);

  if ((tokens === null) ||
    (tokens.length !== 3) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  return {
    source: tokens[0],
    defaultText: tokens[2],
    keywordCase: getKeywordCase(tokens[1]),
  };
}


/**
 * Parses out properties to initialize a keyword helper using current cursor position
 * @param {string} text - input text
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} properties to initialize a keyword helper
 *   if cursor is placed within a valid definition or at the end of a partial definition.
 * Returns undefined; otherwise.
 */
export function parseRule(text, cursorIndex) {
  const processedText = text || '';
  const startIndex = processedText.lastIndexOf('{', cursorIndex);

  if (startIndex === -1 || cursorIndex === startIndex) {
    return null;
  }

  const parsers = [
    {
      input: processedText.substring(startIndex),
      syntax: keywordRuleSyntax,
      inclusive: false,
    },
    {
      input: processedText.slice(startIndex, cursorIndex),
      syntax: keywordRuleInProgressSyntax,
      inclusive: true,
    },
  ];

  let properties = null;

  some(parsers, (parser) => {
    const rule = parseKeywordRule(parser.input, parser.syntax, startIndex, cursorIndex);

    if ((rule !== null) &&
      (cursorIndex < startIndex + rule.source.length + (parser.inclusive ? 1 : 0))) {
      const options = pick(rule, 'defaultText', 'keywordCase');

      options.prefix = processedText.substring(0, startIndex);
      options.suffix = processedText.substring(startIndex + rule.source.length);
      options.startIndex = startIndex;
      options.endIndex = startIndex + rule.source.length;

      properties = {
        type: 'keyword',
        options,
      };

      return true;
    }
    return false;
  });

  return properties;
}
